import React, { useMemo, useState } from 'react';
import { getTranslatedText, AccessControl } from '@nile/components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, ButtonGroup, Spinner } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';
import RollbackConfirmationModal from '../common/RollbackConfirmationModal';

// Configuration for bulk action buttons
const BUTTON_CONFIGS = [
  {
    id: 'approveInstall',
    action: 'ApproveInstall',
    titleKey: 'rmm.patch.approve.and.install',
    iconName: 'Install',
    iconColor: '#011fff'
  },
  {
    id: 'approve',
    action: 'Approve',
    titleKey: 'rmm.patchManagement.bulkActions.approve',
    iconName: 'Check',
    iconColor: '#4F712B'
  },
  {
    id: 'reject',
    action: 'Reject',
    titleKey: 'rmm.patchManagement.bulkActions.reject',
    iconName: 'RejectedCircle',
    iconColor: '#B63535'
  },

  {
    id: 'rollback',
    action: 'Rollback',
    titleKey: 'Rollback',
    iconName: 'Reset',
    iconColor: '#FF7F23'
  }
];

// Common tooltip configuration
const TOOLTIP_CONFIG = {
  inertia: true,
  delay: 300,
  trigger: 'mouseenter',
  animation: 'shift',
  animateFill: false,
  position: 'bottom-start'
};

// Reusable ActionButton component
const ActionButton = React.memo(
  ({ config, disabled, onClick, showLabel, intl }) => {
    const buttonLabel = showLabel
      ? getTranslatedText(intl, config.titleKey)
      : null;

    return (
      <Tooltip
        title={getTranslatedText(intl, config.titleKey)}
        {...TOOLTIP_CONFIG}
      >
        <Button
          disabled={disabled || config.disabled}
          onClick={() => onClick(config.action)}
          size="medium"
          monochrome
          iconColor={config.iconColor}
          icon={<SVGIcon name={config.iconName} color={config.iconColor} />}
        >
          {buttonLabel}
        </Button>
      </Tooltip>
    );
  }
);

ActionButton.displayName = 'ActionButton';

// Loading component
const LoadingSpinner = React.memo(() => (
  <div className="mt-8">
    <Spinner size="small" color="inkLightest" />
  </div>
));

LoadingSpinner.displayName = 'LoadingSpinner';

// Selection count component
const SelectionCount = React.memo(({ count, show }) => {
  if (!show || count === 0) return null;
  
  return (
    <span className="mr-10">
      <Button selected outline size="medium">
        {count}{' '}
        <FormattedMessage id="rmm.patchManagement.bulkActions.selected" />
      </Button>
    </span>
  );
});

SelectionCount.displayName = 'SelectionCount';

function PatchBulkActionNew(props) {
  const {
    actionClick,
    showCount,
    records,
    loader,
    intl,
    showLabel = false,
    disable = false,
    disableActions = [],
    hideActions = [],
    withRollBackConfirmation = false
  } = props;

  const [rollbackModalActive, setRollbackModalActive] = useState(false);

  const recordsCount = useMemo(() => records?.length || 0, [records]);
  const haveRecords = useMemo(() => recordsCount > 0 && !disable, [
    recordsCount,
    disable
  ]);

  const handleActionClick = useMemo(
    () => (action) => {
      if (action?.toLowerCase() === 'rollback' && withRollBackConfirmation) {
        setRollbackModalActive(true);
        return;
      }
      actionClick?.(action);
    },
    [actionClick]
  );

  if (loader) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <AccessControl
        roleToCheck="approveRejectPatches.allow"
        render={
          <div className="fx">
            <SelectionCount
              count={recordsCount}
              show={showCount && haveRecords}
            />
            <ButtonGroup segmented>
              {BUTTON_CONFIGS.map((config) => {
                if (hideActions.includes(config.id)) {
                  return null;
                }
                const isDisabled = disableActions.includes(config.id);
                return (
                  <ActionButton
                    key={config.id}
                    config={{
                      ...config,
                      disabled: isDisabled,
                      iconColor: isDisabled ? '#999999' : config.iconColor
                    }}
                    disabled={!haveRecords}
                    onClick={handleActionClick}
                    showLabel={showLabel}
                    intl={intl}
                  />
                );
              })}
            </ButtonGroup>
          </div>
        }
      />
      <RollbackConfirmationModal
        isOpen={rollbackModalActive}
        onClose={() => setRollbackModalActive(false)}
        onUninstall={() => actionClick?.('uninstall')}
        onRejectAndUninstall={() => actionClick?.('rejectanduninstall')}
      />
    </>
  );
}

export default React.memo(injectIntl(PatchBulkActionNew));
